package dev.lingvo.shared

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder

/**
 * Who did it. A token is not a person, and a webhook is neither.
 *
 * `OPERATOR` is Lingvo support acting inside a customer's workspace from the operator console. It
 * ships here in phase 1 and is not written by anything until phase 3, deliberately: this enum has
 * no default and the browser's `API_JSON` does not coerce, so an `'OPERATOR'` row reaching a cached
 * bundle that lacks the value throws out of the whole `AuditPageDto` rather than one row of it.
 * `:site` must be **deployed** with this before the server that can write one is.
 */
@Serializable(with = ActorKindSerializer::class)
enum class ActorKind { USER, TOKEN, SYSTEM, PADDLE, AGENT, OPERATOR }

/**
 * An actor kind this build has never heard of reads as [ActorKind.SYSTEM] rather than throwing.
 *
 * The server already reads the column exactly this leniently (`AuditRepo.page`, the
 * `runCatching { ActorKind.valueOf(…) }.getOrDefault(ActorKind.SYSTEM)` around line 121). This makes
 * the browser agree, which is the half that matters: the server can be redeployed in a minute and a
 * visitor's cached bundle cannot.
 *
 * Scoped to this one enum on purpose. Turning on `coerceInputValues` globally would silently soften
 * every other enum in the product — a `FileFormat` the client does not know would quietly become
 * whatever the default happened to be, and an import would write the wrong parser's output.
 */
object ActorKindSerializer : KSerializer<ActorKind> {
    override val descriptor: SerialDescriptor =
        PrimitiveSerialDescriptor("dev.lingvo.shared.ActorKind", PrimitiveKind.STRING)

    override fun serialize(encoder: Encoder, value: ActorKind) = encoder.encodeString(value.name)

    override fun deserialize(decoder: Decoder): ActorKind {
        val raw = decoder.decodeString()
        return ActorKind.entries.firstOrNull { it.name == raw } ?: ActorKind.SYSTEM
    }
}

/** The customer-facing category for an audit actor. */
val ActorKind.customerLabel: String
    get() = when (this) {
        ActorKind.USER -> "person"
        ActorKind.TOKEN -> "token"
        ActorKind.AGENT -> "agent"
        ActorKind.SYSTEM -> "automatic"
        ActorKind.PADDLE -> "payments"
        ActorKind.OPERATOR -> "support"
    }

/**
 * One governance event, as a sentence.
 *
 * [sentence] is built server-side from labels frozen at write time — "Maya changed Kai from
 * Translator to Admin" still reads correctly after both accounts are gone, which a join through a
 * nullable foreign key cannot manage.
 */
@Serializable
data class AuditEventDto(
    val id: String,
    val sentence: String,
    val action: String,
    val actorKind: ActorKind,
    val actorLabel: String,
    val targetLabel: String? = null,
    val createdAt: String,
    /** The raw detail, behind a disclosure. Present so nobody has to trust the sentence. */
    val payload: Map<String, String> = emptyMap(),
)

/**
 * A page of the log.
 *
 * Keyset paginated, never `OFFSET`: an append-only log is written to while somebody reads it, and
 * offset paging in that situation silently repeats and skips rows.
 */
@Serializable
data class AuditPageDto(
    val events: List<AuditEventDto> = emptyList(),
    /** Pass back as `after` for the next page. Null when there is none. */
    val nextCursor: String? = null,
    /** How long events are kept on this plan, stated rather than discovered. */
    val retentionDays: Int,
)

/**
 * What Lingvo tells another system about.
 *
 * Deliberately short. `locale.completed` is the event that earns the feature — "German hit 100%,
 * open the release PR" — and the rest are the ones somebody would otherwise poll for.
 */
@Serializable
enum class WebhookEvent(val id: String, val description: String) {
    KEY_CREATED("key.created", "A source string was added"),
    KEY_OBSOLETED("key.obsoleted", "A source string was marked obsolete"),
    TRANSLATION_APPROVED("translation.approved", "A translation was approved"),
    LOCALE_COMPLETED("locale.completed", "Every string in a language is now approved"),
    QA_FAILED("qa.failed", "A QA check started failing"),
    MT_JOB_FINISHED("mt_job.finished", "A machine-translation run finished"),
    IMPORT_FINISHED("import.finished", "An import finished"),
    MEMBER_ROLE_CHANGED("member.role_changed", "Somebody's role changed"),
    ;

    companion object {
        fun byId(id: String): WebhookEvent? = entries.firstOrNull { it.id.equals(id, ignoreCase = true) }
    }
}

@Serializable
data class WebhookDto(
    val id: String,
    val url: String,
    val events: List<WebhookEvent> = emptyList(),
    val enabled: Boolean = true,
    /**
     * The signing secret, returned **once** when it is created or rotated, and never again.
     *
     * One secret at a time, no dual-signing window: that is a feature to add when somebody with two
     * receivers asks for it, and building it first would be an operational surface with no users.
     */
    val secret: String? = null,
    /**
     * Whether deliveries carry a signature the receiver can actually check.
     *
     * False for a webhook created before the secret was stored in a form that can sign — those were
     * signed with a hash of the secret rather than the secret, so `HMAC(secret, body)` never
     * matched and no receiver on earth could verify one. The plaintext is gone, so the only repair
     * is to rotate. Surfaced rather than left in a changelog: the workspace that has one cannot
     * discover this by looking at its own deliveries, which all say 200.
     */
    val signatureVerifiable: Boolean = true,
    val createdAt: String,
    /** The last delivery's outcome, so a broken endpoint is visible without opening a log. */
    val lastStatus: Int? = null,
    val lastAttemptAt: String? = null,
    val consecutiveFailures: Int = 0,
)

@Serializable
data class CreateWebhookRequest(
    val url: String,
    val events: List<WebhookEvent> = emptyList(),
)

/**
 * One attempt to reach a webhook.
 *
 * **The payload is deliberately absent.** It is customer content — frequently the string that just
 * changed — and the questions this log answers are "did it arrive", "what came back" and "when".
 * None of them need the body, and re-serving it would make a delivery log a second copy of the data.
 */
@Serializable
data class WebhookDeliveryDto(
    val id: String,
    /** Null if the event was renamed or removed since. The row is still worth showing. */
    val event: WebhookEvent? = null,
    val attempts: Int = 0,
    /**
     * Whether it actually arrived.
     *
     * Stated rather than inferred: a delivery that burns through its attempts also stops being
     * pending, so "finished" and "succeeded" are different facts and a page that conflates them
     * reports failures as deliveries.
     */
    val succeeded: Boolean = false,
    val finishedAt: String? = null,
    val status: Int? = null,
    val error: String? = null,
    /** Set only while it is still queued. */
    val nextAttemptAt: String? = null,
    val createdAt: String,
)

@Serializable
data class WebhookDeliveriesDto(val deliveries: List<WebhookDeliveryDto> = emptyList())
